import { ServiceMode } from 'generated/graphql-gateway';
import { ISystemwideOffersFragment, ISystemwideTierGroupFragment } from 'generated/sanity-graphql';
import { ILocalizedMenuEntry, IndexedMenu, PosServiceMode } from 'state/unified-menu/types';

import {
  OfferWithPrice,
  SystemwideOffer,
  SystemwideTierGroup,
  TierGroupLog,
  getPriceTargets,
  selectOffer,
  validateTierGroup,
} from './base';

function getEntryPrice(
  entryId: string | undefined,
  indexedMenu: IndexedMenu,
  posServiceMode: PosServiceMode
): number {
  if (!entryId) {
    return 0;
  }
  const menuEntry = indexedMenu.find(entryId) as ILocalizedMenuEntry | undefined;
  return menuEntry?.price?.[posServiceMode] ?? 0;
}

function getMenuItemsPrice(
  tierGroup: SystemwideTierGroup,
  indexedMenu: IndexedMenu,
  posServiceMode: PosServiceMode
): number {
  if (!tierGroup.menu_items || !tierGroup.menu_items.length) {
    return 0;
  }

  let totalPrice = 0;
  for (const menuItem of tierGroup.menu_items) {
    // menuItem is IComboPlusFragment | IItemFragment, which should have an _id
    if (menuItem?._id) {
      const price = getEntryPrice(menuItem._id, indexedMenu, posServiceMode);
      if (typeof price === 'number' && !isNaN(price)) {
        totalPrice += price;
      }
    }
  }
  return totalPrice;
}

function getOfferPrice(
  offer: ISystemwideOffersFragment,
  indexedMenu: IndexedMenu,
  posServiceMode: PosServiceMode
): number {
  if (!offer?._id) {
    return 0;
  }
  const price = getEntryPrice(offer._id, indexedMenu, posServiceMode);
  if (typeof price !== 'number' || isNaN(price)) {
    return 0;
  }
  return price;
}

export function processTierGroup_UnifiedMenu(
  tierGroup: ISystemwideTierGroupFragment,
  indexedMenu: IndexedMenu,
  posServiceMode: PosServiceMode
): SystemwideOffer | null {
  const [validatedTierGroup, validationError] = validateTierGroup(tierGroup);
  if (!validatedTierGroup) {
    // eslint-disable-next-line no-console -- TODO: Remove when BKPE-19977 is done
    console.warn(
      '>>> Invalid tier group found, skipping:',
      tierGroup?.tierGroupName,
      ' Error:',
      validationError
    );
    return null;
  }

  const itemsPrice = getMenuItemsPrice(validatedTierGroup, indexedMenu, posServiceMode);
  const priceTargets = getPriceTargets(validatedTierGroup, itemsPrice);

  const offersWithPrice: OfferWithPrice[] = validatedTierGroup.offers
    .map(offer => {
      const price = getOfferPrice(offer, indexedMenu, posServiceMode);
      return price >= 0 ? { offer, price } : null;
    })
    .filter((item): item is OfferWithPrice => item !== null);

  const [selectedOffer, selectionError] = selectOffer(priceTargets, offersWithPrice);

  const log: TierGroupLog = {
    priceTargets,
    offersWithPrice,
  };

  selectedOffer && (log.selectedOffer = selectedOffer);
  selectionError && (log.error = selectionError);

  // eslint-disable-next-line no-console -- TODO: Remove when BKPE-19977 is done
  console.log(tierGroup.tierGroupName, log);

  return selectedOffer;
}

export type TransformTierGroupsToOffersOptions_UnifiedMenu = {
  items: (ISystemwideOffersFragment | ISystemwideTierGroupFragment)[];
  indexedMenu: IndexedMenu;
  serviceMode: ServiceMode;
};

export function transformTierGroupsToOffers_UnifiedMenu({
  items,
  indexedMenu,
  serviceMode,
}: TransformTierGroupsToOffersOptions_UnifiedMenu): ISystemwideOffersFragment[] {
  if (!items.length) {
    return [];
  }

  // eslint-disable-next-line no-console -- TODO: Remove when BKPE-19977 is done
  console.group('>>> TierGroup (Unified Menu)');

  const posServiceMode: PosServiceMode = serviceMode === 'DELIVERY' ? 'delivery' : 'pickup';
  // eslint-disable-next-line no-console -- TODO: Remove when BKPE-19977 is done
  console.log('Input', {
    serviceMode,
    posServiceMode,
  });

  const transformed = items
    .map(item => {
      if (item.__typename !== 'SystemwideTierGroup') {
        return item; // Pass through regular offers
      }
      return processTierGroup_UnifiedMenu(item, indexedMenu, posServiceMode);
    })
    .filter((offer): offer is ISystemwideOffersFragment => offer !== null);

  // eslint-disable-next-line no-console -- TODO: Remove when BKPE-19977 is done
  console.groupEnd();
  return transformed;
}
